/**
 * HƯỚNG DẪN LẤY API TOKEN & ACCOUNT ID — `docs/06 §6.2`.
 *
 * Vì sao có màn này: `docs/06 §6.2` viết *"Khi người dùng thêm token, **hướng
 * dẫn họ tạo** với các permission sau"*. Trước đó phần "hướng dẫn" chỉ tồn tại
 * dưới dạng một câu phụ đề nêu **hai** trong **sáu** quyền — người làm theo sẽ
 * thiếu SSL/HSTS, xoá cache và cả khả năng tự lấy `account_id` (OQ-114).
 *
 * Danh sách quyền dưới đây chép **nguyên văn** bảng của `docs/06 §6.2`, kể cả
 * cột "Dùng cho": người đọc cần biết thiếu quyền nào thì mất chức năng nào, chứ
 * không chỉ biết phải tick ô nào.
 *
 * ── OQ-284 · Bảng này từng THIẾU một quyền, và nó đã cắn người dùng thật ───
 *
 * Bản trước gộp `Zone Settings` vào `SSL and Certificates` — dòng
 * `SSL and Certificates → Edit` mang chú thích *"SSL mode, HSTS"*. Nhưng SSL
 * mode và HSTS đi qua `PATCH /zones/:id/settings/:key`, cần **`Zone Settings`**;
 * còn `SSL and Certificates` chỉ dùng cho `/ssl/certificate_packs`.
 *
 * Chủ dự án tạo token theo đúng bảng này, rồi bấm Lưu ở tab SSL/TLS và nhận
 * `9109` — mất hai lượt truy nguyên (OQ-271) mới ra, vì mọi thứ khác đều đúng.
 *
 * > Ở OQ-271 tôi sửa **triệu chứng** (thông điệp lỗi nói rõ "thiếu quyền") mà
 * > không lần tới **nguồn**: chính hướng dẫn này dạy người ta tạo token thiếu.
 * > Một thông điệp lỗi tốt cứu được người đã hỏng; sửa hướng dẫn thì không ai
 * > hỏng nữa.
 *
 * ── OQ-285 · `account_id` KHÔNG còn nhập tay được ─────────────────────────
 *
 * Màn thêm kết nối đã bỏ ô nhập `Account ID`; máy chủ tự lấy qua `listAccounts()`
 * lúc kiểm tra token. Nên chú thích cũ *"Account ID có thể để trống… muốn điền
 * tay thì lấy ở góc phải trang tổng quan"* mô tả một đường đi **không còn tồn
 * tại** — nó chỉ người dùng đi tìm một ô không có trên màn hình.
 *
 * Quan trọng hơn: `Account Settings → Read` từ chỗ *tiện lợi* thành **bắt
 * buộc**. Thiếu nó thì `accountId = null`, và `POST /zones` (thêm tên miền mới)
 * gửi `account.id: null` — Cloudflare từ chối. Không còn đường vòng nào.
 */

import { useState } from 'react';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';

/**
 * `docs/06 §6.2` — bảy dòng, đúng thứ tự tài liệu.
 *
 * Cột `perm` giữ **nguyên tiếng Anh** ở cả hai ngôn ngữ: đó là chữ người dùng
 * phải tìm thấy trên trang Cloudflare, và Cloudflare không có bản tiếng Việt.
 * Dịch nó ra là dạy người ta tìm một dòng không tồn tại.
 *
 * Cột "Dùng cho" thì ngược lại — nó giải thích, nên phải theo ngôn ngữ đang
 * chọn (OQ-290).
 */
const QUYEN: { perm: string; khoa: string; tuyChon?: true }[] = [
  { perm: 'Zone → Zone → Read', khoa: 'conn.guidePerm.zoneRead' },
  { perm: 'Zone → Zone → Edit', khoa: 'conn.guidePerm.zoneEdit' },
  { perm: 'Zone → DNS → Edit', khoa: 'conn.guidePerm.dnsEdit' },
  { perm: 'Zone → Zone Settings → Edit', khoa: 'conn.guidePerm.zoneSettings' },
  { perm: 'Zone → SSL and Certificates → Read', khoa: 'conn.guidePerm.sslRead' },
  { perm: 'Zone → Cache Purge → Purge', khoa: 'conn.guidePerm.cachePurge' },
  { perm: 'Account → Account Settings → Read', khoa: 'conn.guidePerm.accountRead' },
  /*
   * OQ-337 — quyen DUY NHAT trong bang la tuy chon.
   *
   * Thieu no thi khong co canh bao nao (`missingScopes` khong kiem), vi phan
   * lon ket noi khong dung Tunnel. Nhung nguoi doc bang nay dang NGOI TAO
   * token — day dung luc re nhat de tick them mot o, so voi viec vai tuan sau
   * phai vao Cloudflare sua token roi quay lai bam Xac minh.
   */
  { perm: 'Account → Cloudflare Tunnel → Edit', khoa: 'conn.guidePerm.tunnel', tuyChon: true },
];

const TRANG_TOKEN = 'https://dash.cloudflare.com/profile/api-tokens';

export function TokenGuide() {
  const t = useT();
  const [mo, setMo] = useState(false);

  return (
    <div className="token-guide" data-od-id="token-guide">
      {/*
        Kiểu CẢNH BÁO chứ không phải nút mờ (OQ-284).
        
        Trước đây đây là `btn btn-ghost` — chữ xám nhạt lẫn vào chú thích ngay
        trên nó. Người chưa có token lướt qua mà không thấy, rồi tự mò tạo token
        trên Cloudflare và tick thiếu quyền. Đúng con đường dẫn tới sự cố
        OQ-271, chỉ khác là lần ấy hướng dẫn cũng sai.
        
        `aria-expanded` giữ nguyên: nổi bật là chuyện thị giác, không được đổi
        thứ trình đọc màn hình nghe thấy.
      */}
      <button
        type="button"
        className={`token-guide-toggle ${mo ? 'dang-mo' : ''}`}
        aria-expanded={mo}
        onClick={() => setMo((truoc) => !truoc)}
      >
        <Icon name={mo ? 'info' : 'warn'} size={16} />
        <span>{mo ? t('conn.guideHide') : t('conn.guideShow')}</span>
      </button>

      {mo ? (
        <div className="scope-note token-guide-body">
          <Icon name="key" size={17} />
          <div className="grow">
            <b>{t('conn.guideTitle')}</b>

            <ol className="token-guide-steps">
              <li>
                {t('conn.guideStep1')}{' '}
                <a href={TRANG_TOKEN} target="_blank" rel="noreferrer noopener">
                  dash.cloudflare.com/profile/api-tokens
                  <Icon name="external" size={13} />
                </a>
              </li>
              <li>{t('conn.guideStep2')}</li>
              <li>{t('conn.guideStep3')}</li>
              <li>{t('conn.guideStep4')}</li>
            </ol>

            <div className="table-wrap">
              <table className="tbl token-guide-perms">
                <thead>
                  <tr>
                    <th scope="col">{t('conn.guidePerm')}</th>
                    <th scope="col">{t('conn.guidePermUse')}</th>
                  </tr>
                </thead>
                <tbody>
                  {QUYEN.map((row) => (
                    <tr key={row.perm}>
                      <td>
                        <code className="mono">{row.perm}</code>
                        {row.tuyChon ? (
                          <span className="chip" data-od-id="perm-optional">
                            {t('conn.guidePermOptional')}
                          </span>
                        ) : null}
                      </td>
                      <td className="muted">{t(row.khoa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="token-guide-foot">{t('conn.guideSecurity')}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
